import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createProjectFolder,
  deleteProjectFolder,
  fetchProjectFolders,
  updateProjectFolder,
} from "../../api/project/folder";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  totalCount: 0,
  currentCount: 0,
};

export const initialController = {
  filters: [],
  page: 1,
  per_page: 200,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const resetProjectFolders = createAsyncThunk(
  "projectFolders/resetProjectFolders",
  async () => {
    return null;
  }
);

export const deleteProjectFolderAsync = createAsyncThunk(
  "projectFolders/deleteProjectFolderAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteProjectFolder(payload);
      return response?.data ?? response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_project_folder");
    }
  }
);

export const createProjectFolderAsync = createAsyncThunk(
  "projectFolders/createProjectFolderAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createProjectFolder(payload);
      const data = response?.data ?? response;
      if (data?.error || hasMeaningfulErrors(data?.errors)) {
        return rejectNormalized(rejectWithValue, data, "create_project_folder");
      }
      return data;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_project_folder");
    }
  }
);

export const updateProjectFolderAsync = createAsyncThunk(
  "projectFolders/updateProjectFolderAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateProjectFolder(payload);
      const data = response?.data ?? response;
      const status = response?.status ?? data?.status;
      if (
        isHttpFailureStatus(status) ||
        data?.error ||
        hasMeaningfulErrors(data?.errors)
      ) {
        return rejectNormalized(rejectWithValue, data, "update_project_folder");
      }
      return data;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_project_folder");
    }
  }
);

export const getProjectFoldersAsync = createAsyncThunk(
  "projectFolders/getProjectFoldersAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchProjectFolders(controller);
      const data = response?.data ?? response;
      const status = response?.status ?? data?.status;
      if (
        isHttpFailureStatus(status) ||
        hasMeaningfulErrors(data?.errors)
      ) {
        return rejectNormalized(rejectWithValue, data, "list_project_folders");
      }
      return data;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_project_folders");
    }
  }
);

const projectFolderSlice = createSlice({
  name: "projectFolders",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProjectFoldersAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getProjectFoldersAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload?.data ?? [];
        state.totalCount = action.payload?.totalCount ?? 0;
        state.currentCount = action.payload?.currentCount ?? 0;
      })
      .addCase(getProjectFoldersAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke hente prosjektmapper."
        );
      })
      .addCase(updateProjectFolderAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateProjectFolderAsync.fulfilled, (state) => {
        return produce(state, (draftState) => {
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(updateProjectFolderAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke oppdatere prosjektmappen."
        );
      })
      .addCase(createProjectFolderAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createProjectFolderAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          if (action.payload?.id) {
            draftState.data.push(action.payload);
          }
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(createProjectFolderAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke opprette prosjektmappen."
        );
      })
      .addCase(deleteProjectFolderAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteProjectFolderAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id = action.meta?.arg?.id;
          if (id != null && Array.isArray(draftState.data)) {
            draftState.data = draftState.data.filter((row) => row.id !== id);
          } else if (action.payload?.data) {
            draftState.data = action.payload.data;
          }
          draftState.isLoading = false;
        });
      })
      .addCase(deleteProjectFolderAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke slette prosjektmappen."
        );
      })
      .addCase(resetProjectFolders.pending, (state) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(resetProjectFolders.fulfilled, (state) => {
        state.isLoading = false;
        state.data = [];
        state.totalCount = 0;
        state.currentCount = 0;
      })
      .addCase(resetProjectFolders.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
        state.errorMessages = [action.payload?.error].filter(Boolean);
      });
  },
});

export const { reducer } = projectFolderSlice;

export default projectFolderSlice;
